import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  Calendar,
  CreditCard,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayoutPage = () => {
  const { user } = useAuth();
  const [payoutData, setPayoutData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const fetchPayoutData = async () => {
    try {
      const [payoutRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/pending-payout`),
        axios.get(`${API}/admin/payout-history`)
      ]);
      setPayoutData(payoutRes.data);
      setHistory(historyRes.data.payout_history || []);
    } catch (error) {
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-payout-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight">
          Payout Dashboard
        </h1>
        <p className="text-ink-500 mt-1">View pending and historical payouts</p>
      </div>

      {/* Pending Payout Card */}
      <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-coral-500/30">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <p className="text-ink-700 text-sm font-medium mb-2">Pending Payout</p>
            <p className="text-5xl font-bold text-ink-950">
              ${payoutData?.total_pending_payout?.toFixed(2) || "0.00"}
            </p>
            <p className="text-ink-500 mt-2">
              For {payoutData?.current_month}
            </p>
          </div>
          <div className="text-right">
            <p className="text-ink-500 text-sm">Next Payout Date</p>
            <p className="text-xl font-semibold text-ink-950">{payoutData?.next_payout_date}</p>
            <p className="text-sm text-ink-500 mt-1">{payoutData?.payout_destination}</p>
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Free Plan</span>
            <div className="w-8 h-8 rounded-lg bg-ink-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-ink-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-ink-950">{payoutData?.breakdown?.free?.count || 0}</p>
          <p className="text-sm text-ink-500">${payoutData?.breakdown?.free?.total?.toFixed(2) || "0.00"}</p>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Premium Plan</span>
            <div className="w-8 h-8 rounded-lg bg-coral-500/20 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-coral-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-coral-500">{payoutData?.breakdown?.premium?.count || 0}</p>
          <p className="text-sm text-ink-500">${payoutData?.breakdown?.premium?.total?.toFixed(2) || "0.00"}</p>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-ink-200">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-ink-500">Platinum Plan</span>
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-amber-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-400">{payoutData?.breakdown?.platinum?.count || 0}</p>
          <p className="text-sm text-ink-500">${payoutData?.breakdown?.platinum?.total?.toFixed(2) || "0.00"}</p>
        </div>
      </div>

      {/* Payout History */}
      <div className="p-6 rounded-2xl bg-white border border-ink-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ink-950">Payout History</h3>
          <Button variant="outline" size="sm" className="border-ink-200 gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-ink-500 mx-auto mb-2" />
            <p className="text-ink-500">No payout history yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">Month</th>
                  <th className="text-left py-3 px-4 text-sm text-ink-500 font-medium">Subscriptions</th>
                  <th className="text-right py-3 px-4 text-sm text-ink-500 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={index} className="border-b border-ink-200/50">
                    <td className="py-3 px-4 text-ink-950">{item._id}</td>
                    <td className="py-3 px-4 text-ink-700">{item.count}</td>
                    <td className="py-3 px-4 text-right text-coral-500 font-semibold">
                      ${item.total?.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 rounded-xl bg-white border border-ink-200">
        <p className="text-sm text-ink-500">
          <span className="text-ink-950 font-medium">Note:</span> All subscription payments are collected throughout the month and transferred to your PayPal Business account on the 1st of each month.
        </p>
      </div>
    </div>
  );
};

export default AdminPayoutPage;
